/**
 * Sentry Error-Monitoring — Frontend Initialisierung
 *
 * Muss VOR dem React-Render in main.tsx importiert werden,
 * damit alle Fehler ab Seitenstart erfasst werden.
 */

import * as Sentry from '@sentry/react';

const SENTRY_DSN = import.meta.env.VITE_SENTRY_DSN;

export function initSentry() {
  if (!SENTRY_DSN) {
    console.warn('[Sentry] VITE_SENTRY_DSN nicht gesetzt — Error-Monitoring deaktiviert');
    return;
  }

  Sentry.init({
    dsn: SENTRY_DSN,
    environment: import.meta.env.MODE, // 'development' | 'production'
    release: import.meta.env.VITE_SENTRY_RELEASE || undefined,

    // Nur 10% der Transactions für Performance-Monitoring (optional)
    tracesSampleRate: import.meta.env.PROD ? 0.1 : 1.0,

    // Session Replay für besseres Debugging (nur in Production)
    replaysSessionSampleRate: 0,
    replaysOnErrorSampleRate: import.meta.env.PROD ? 1.0 : 0,

    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration(),
    ],

    // Fehler von Browser-Extensions und Drittanbieter-Skripten ignorieren
    ignoreErrors: [
      'ResizeObserver loop',
      'ResizeObserver loop completed with undelivered notifications',
      /^Non-Error promise rejection captured/,
      /^Loading chunk .* failed/,
    ],

    beforeSend(event) {
      // In Entwicklung: Fehler nur loggen, nicht senden
      if (import.meta.env.DEV) {
        console.debug('[Sentry] Event (dev-only):', event);
        return null;
      }
      return event;
    },
  });
}

/**
 * Sentry User-Kontext setzen (nach Login aufrufen)
 */
export function setSentryUser(user: { id: string; email: string; rolle?: string }) {
  Sentry.setUser({
    id: user.id,
    email: user.email,
    rolle: user.rolle,
  } as Sentry.User);
}

/**
 * Sentry User-Kontext zurücksetzen (bei Logout aufrufen)
 */
export function clearSentryUser() {
  Sentry.setUser(null);
}

export { Sentry };
